import classNames from "classnames";
import { Divider } from "components/design/Divider";
import { Button } from "components/DesignSystem/Button/Button";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { ArrowDownRed } from "components/icons/ArrowDownRed";
import { ExclamationFadedCircle } from "components/icons/ExclamationFadedCircle";
import { VARIANCE_CHIP_SENTIMENT } from "constants/reports";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";
import {
  VarianceExplanation,
  VarianceExplanationChip,
} from "types/Models/report";

type ReportSummaryCardProps = {
  isLoading: boolean;
  isError: boolean;
  summary?: VarianceExplanation;
  period: string;
  onRetry: () => void;
};

type SummaryChipProps = {
  chip: VarianceExplanationChip;
  isFirst: boolean;
};

const SummaryChip = ({ chip, isFirst }: SummaryChipProps) => {
  const isUnfavorable = chip.sentiment === VARIANCE_CHIP_SENTIMENT.UNFAVORABLE;
  const isFavorable = chip.sentiment === VARIANCE_CHIP_SENTIMENT.FAVORABLE;

  const varianceColorClass = classNames("t-text-body-sm", {
    "t-text-red-70": isUnfavorable,
    "t-text-green-80": isFavorable,
    "t-text-text-30": !isUnfavorable && !isFavorable,
  });

  return (
    <>
      {!isFirst && (
        <Divider
          orientation="vertical"
          type="dashed"
          classNames="t-self-stretch sm:t-hidden"
        />
      )}
      <div className="t-flex t-min-w-0 t-flex-1 t-flex-col t-gap-1.5 t-pl-4 sm:t-pl-2 sm:t-gap-1">
        <div className="t-flex t-items-center t-gap-1">
          <span className="t-text-body sm:t-text-caption t-text-text-30">
            {chip.label}
          </span>
        </div>
        {chip.value && (
          <span className="t-truncate t-text-title-h3 sm:t-text-subtext t-text-text-100">
            {chip.value}
          </span>
        )}
        {chip.variance && (
          <span
            className={classNames(
              "t-flex t-items-center t-gap-1 t-text-body-sm sm:t-text-caption",
              varianceColorClass
            )}
          >
            {isUnfavorable && <ArrowDownRed className="t-text-red-70" />}
            {isFavorable && (
              <span className="t-rotate-x-180">
                <ArrowDownRed className="t-text-green-80" />
              </span>
            )}
            {chip.variance}
          </span>
        )}
      </div>
    </>
  );
};

type ReportSummaryErrorProps = {
  period: string;
  onRetry: () => void;
};

const ReportSummaryError = ({ period, onRetry }: ReportSummaryErrorProps) => (
  <div className="t-flex t-flex-col t-items-center t-gap-3 t-py-4 t-text-center">
    <ExclamationFadedCircle />
    <div className="t-flex t-flex-col t-gap-1">
      <span className="t-text-subtext t-text-text-100">
        Couldn&apos;t generate overview
      </span>
      <span className="t-text-body-sm t-text-text-30">
        Something went wrong putting together {period}. Give it another try.
      </span>
    </div>
    <Button customType="secondary" size="small" onClick={onRetry}>
      Regenerate Overview
    </Button>
  </div>
);

type ReportSummaryContentProps = {
  summary: VarianceExplanation;
  visibleChips: VarianceExplanationChip[];
};

const ReportSummaryContent = ({
  summary,
  visibleChips,
}: ReportSummaryContentProps) => (
  <div className="t-flex t-min-w-0 t-flex-col t-gap-4">
    <p className="t-m-0 t-text-body-sm t-text-text-30 t-whitespace-normal t-break-words">
      {summary.description}
    </p>
    {visibleChips.length > 0 && (
      <div className="t-flex t-items-stretch t-rounded-lg t-border t-border-solid t-border-purple-10 t-bg-surface t-py-4 sm:t-grid sm:t-grid-cols-2 sm:t-gap-y-3 sm:t-py-3">
        {visibleChips.map((chip, index) => (
          <SummaryChip key={chip.label} chip={chip} isFirst={index === 0} />
        ))}
      </div>
    )}
  </div>
);

const ReportSummarySkeleton = () => (
  <div className="t-flex t-min-w-0 t-flex-col t-gap-4">
    <div className="t-flex t-flex-col t-gap-2">
      <Skeleton className="t-h-4 t-w-full" />
      <Skeleton className="t-h-4 t-w-full" />
      <Skeleton className="t-h-4 t-w-2/3" />
    </div>
    <div className="t-flex t-items-stretch t-rounded-lg t-border t-border-solid t-border-purple-10 t-bg-surface t-py-4">
      {Array.from({ length: 4 }, (_, index) => (
        <div
          key={index}
          className={classNames(
            "t-flex t-min-w-0 t-flex-1 t-flex-col t-gap-2 t-pl-4",
            {
              "t-border-0 t-border-l t-border-dashed t-border-neutral-10":
                index > 0,
            }
          )}
        >
          <Skeleton className="t-h-3 t-w-16" />
          <Skeleton className="t-h-5 t-w-20" />
          <Skeleton className="t-h-3 t-w-24" />
        </div>
      ))}
    </div>
  </div>
);

export const ReportSummaryCard = ({
  isLoading,
  isError,
  summary,
  period,
  onRetry,
}: ReportSummaryCardProps) => {
  const visibleChips = (summary?.chips || []).filter((chip) => chip.value);
  const showErrorState = !isLoading && !summary && isError;

  let content = null;
  if (isLoading) content = <ReportSummarySkeleton />;
  if (!isLoading && summary)
    content = (
      <ReportSummaryContent summary={summary} visibleChips={visibleChips} />
    );
  if (showErrorState)
    content = <ReportSummaryError period={period} onRetry={onRetry} />;

  return (
    <div className="t-flex t-w-full t-min-w-0 t-max-w-full t-flex-col t-gap-3 t-rounded-lg t-border t-border-solid t-border-purple-10 t-bg-purple--1 t-p-4 sm:t-px-3">
      {!showErrorState && (
        <div className="t-flex t-items-center t-gap-2">
          <img
            src={AIGenerateButtonStar}
            alt="AIGenerateButtonStar"
            className="t-size-4"
          />
          <span className="t-bg-ai-gradient t-bg-clip-text t-text-subtitle-sm t-text-surface-transparent">
            {period} — what moved & why
          </span>
        </div>
      )}

      {content}
    </div>
  );
};
